import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Article as ArticleIcon,
  Event as EventIcon,
  Email as EmailIcon,
  ContactMail as ContactIcon,
  Logout as LogoutIcon,
  Schedule as CronIcon,
  TrendingUp as FollowUpIcon,
} from '@mui/icons-material';
import { getCurrentUser, signOut, isAdmin } from '../lib/supabase';
import DashboardOverview from '../components/admin/DashboardOverview';
import ArticlesManager from '../components/admin/ArticlesManager';
import SpeakingEventsManager from '../components/admin/SpeakingEventsManager';
import NewsletterManager from '../components/admin/NewsletterManager';
import ContactsManager from '../components/admin/ContactsManager';
import CronJobsManager from '../components/admin/CronJobsManager';
import FollowUpManager from '../components/admin/FollowUpManager';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const user = await getCurrentUser();
      if (!user) {
        navigate('/admin/login');
        return;
      }

      const adminStatus = await isAdmin();
      if (!adminStatus) {
        navigate('/');
        return;
      }

      setUserEmail(user.email || '');
      setLoading(false);
    } catch (error) {
      console.error('Auth check failed:', error);
      navigate('/admin/login');
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/admin/login');
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ bgcolor: '#1a1a1a', boxShadow: 'none', borderBottom: '4px solid #dc143c' }}>
        <Toolbar sx={{ py: 1 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontFamily: '"Impact", "Arial Black", sans-serif',
              fontWeight: 900,
              fontSize: '1.5rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Admin Dashboard
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mr: 3,
              fontFamily: '"Arial", sans-serif',
            }}
          >
            {userEmail}
          </Typography>
          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{
              borderRadius: 0,
              border: '2px solid #dc143c',
              bgcolor: '#dc143c',
              fontFamily: '"Arial Black", sans-serif',
              fontWeight: 700,
              '&:hover': {
                bgcolor: '#b01030',
                borderColor: '#b01030',
              },
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 0, mb: 4, flex: 1 }}>
        <Box sx={{ borderBottom: '3px solid #dc143c', bgcolor: 'white' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="admin tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                fontFamily: '"Arial Black", sans-serif',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.9rem',
                color: '#666',
                '&.Mui-selected': {
                  color: '#dc143c',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#dc143c',
                height: 3,
              },
            }}
          >
            <Tab icon={<DashboardIcon />} label="Overview" iconPosition="start" />
            <Tab icon={<ArticleIcon />} label="Articles" iconPosition="start" />
            <Tab icon={<EventIcon />} label="Speaking Events" iconPosition="start" />
            <Tab icon={<EmailIcon />} label="Newsletter" iconPosition="start" />
            <Tab icon={<ContactIcon />} label="Contacts" iconPosition="start" />
            <Tab icon={<FollowUpIcon />} label="Follow-ups" iconPosition="start" />
            <Tab icon={<CronIcon />} label="Cron Jobs" iconPosition="start" />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <DashboardOverview />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <ArticlesManager />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <SpeakingEventsManager />
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <NewsletterManager />
        </TabPanel>

        <TabPanel value={currentTab} index={4}>
          <ContactsManager />
        </TabPanel>

        <TabPanel value={currentTab} index={5}>
          <FollowUpManager />
        </TabPanel>

        <TabPanel value={currentTab} index={6}>
          <CronJobsManager />
        </TabPanel>
      </Container>
    </Box>
  );
};

export default AdminDashboardPage;